import { BottomLeftFeedback } from '../ActionSuccessToast'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useOptionalCommands } from '../../commands/CommandProvider.jsx'
import PageCommandContextMenu from './PageCommandContextMenu.jsx'
import { NotificationCard } from '../NotificationCenter.jsx'

const PAGE_ID = 'position-workspace'
const FOCUS_COMMAND_IDS = [
  'position.actions.focus',
  'position.move_stop.focus',
  'position.left_column.focus',
  'position.right_column.focus',
]
const NOOP_REGISTER_COMMAND_HANDLER = () => () => {}

export function commandSettingsPath(pageId, commandId = null) {
  const params = new URLSearchParams({ area: 'workspace', commandPage: pageId })
  if (commandId) params.set('command', commandId)
  return `/settings?${params.toString()}`
}

export default function PositionWorkspaceCommandSurface({
  currentSymbol,
  assetIdeas,
  onShowActions,
  onShowLeftColumn,
  onShowRightColumn,
  children,
  ...surfaceProps
}) {
  const commandContext = useOptionalCommands()
  const registerCommandHandler = commandContext?.registerCommandHandler || NOOP_REGISTER_COMMAND_HANDLER
  const rootRef = useRef(null)
  const [menu, setMenu] = useState(null)
  const [pendingFocusCommandId, setPendingFocusCommandId] = useState(null)

  const closeMenu = useCallback(() => setMenu(null), [])
  const showAndFocus = useCallback((commandId) => {
    if (commandId === 'position.left_column.focus') onShowLeftColumn?.()
    else if (commandId === 'position.right_column.focus') onShowRightColumn?.()
    else onShowActions?.()
    setPendingFocusCommandId(commandId)
    return { ok: true }
  }, [onShowActions, onShowLeftColumn, onShowRightColumn])

  useEffect(() => {
    const unregister = FOCUS_COMMAND_IDS.map(commandId => (
      registerCommandHandler(commandId, () => showAndFocus(commandId))
    ))
    return () => unregister.forEach(dispose => dispose())
  }, [registerCommandHandler, showAndFocus])

  useEffect(() => {
    if (!pendingFocusCommandId) return undefined
    const frame = requestAnimationFrame(() => {
      const target = rootRef.current?.querySelector(`[data-command-target="${pendingFocusCommandId}"]`)
      target?.scrollIntoView?.({ block: 'nearest' })
      target?.focus?.({ preventScroll: true })
      setPendingFocusCommandId(null)
    })
    return () => cancelAnimationFrame(frame)
  }, [pendingFocusCommandId])

  useEffect(() => {
    if (!assetIdeas?.add) return undefined
    return registerCommandHandler('position.ideas.add_current_asset', () => assetIdeas.add(currentSymbol))
  }, [registerCommandHandler, assetIdeas?.add, currentSymbol])

  const handleContextMenu = (event) => {
    if (event.shiftKey) return
    const assetSymbol = event.target?.closest?.('[data-asset-symbol]')?.getAttribute('data-asset-symbol') || null
    if (!assetSymbol || !assetIdeas?.add) return
    event.preventDefault()
    setMenu({
      x: event.clientX,
      y: event.clientY,
      pageId: PAGE_ID,
      assetSymbol,
    })
  }

  return (
    <div ref={rootRef} onContextMenu={handleContextMenu} {...surfaceProps}>
      {children}
      {assetIdeas?.notice && (
        <BottomLeftFeedback key={assetIdeas.notice.id}><div role={assetIdeas.notice.tone === 'error' ? 'alert' : 'status'} className={`transition-opacity duration-[250ms] motion-reduce:transition-none ${assetIdeas.notice.fading ? 'pointer-events-none opacity-0' : 'opacity-100'}`}>
          <NotificationCard
            notification={{ tone: assetIdeas.notice.tone, title: assetIdeas.notice.text }}
            onDismiss={assetIdeas.dismissNotice}
            showMeta={false}
            testId="idea-confirmation-toast"
          />
        </div></BottomLeftFeedback>
      )}
      <PageCommandContextMenu
        menu={menu}
        onAddToIdeas={assetIdeas?.add}
        onClose={closeMenu}
      />
    </div>
  )
}
